import "./GerberFileEditor.css"
import {observer} from "mobx-react-lite"
import {fileEditorStore} from "../../stores/FileEditorStore";
import React, {MutableRefObject, useEffect, useRef} from "react";
import {toJS} from "mobx";
import {globalContext} from "./GlobalContext";
import editorService from "../../service/EditorService";
import EditorCommandFactory from "../editor/EditorCommandFactory"
import {DrillPoint, EditorModeType, PaintData, PolyLine} from "../../service/Editor";
import EditorElement from "./EditorElement";

const pointerOffsetX = 270
const pointerOffsetY = 80
const pointerWidth = 16

const canvasToSvgKoeff = 10.0
const maxZoom = 50
const minZoom = 0.1
const zoomSpeed = 100
const zoomSpeedFactor = 0.5

const minGridSize = 20
const gridColor = "#bf00ff"
const drillColor = "#2d2826"
const routesColor = "#8c8301"
const cutColor = "#2d2826"

const drawDefaultContext = {
    paintingMode: false,
    pointerX: -50,
    pointerY: -50,
    nonStickedPointerX: 0,
    nonStickedPointerY: 0,
    moveMode: false,
    zoom: 1.0,
    moveOriginX: 0,
    moveOriginY: 0,
    currentX: 0,
    currentY: 0,
    targetLeft: 0,
    targetTop: 0,
    currentBgX: 0,
    currentBgY: 0,
    paintDataLastX: 0,
    paintDataLastY: 0,
    originZoom: 1
}

const drawContext = {
    ...drawDefaultContext
}

let initializedFileName: string = ""

type GridContext = {
    constAdjX: number,
    constAdjY: number,
    gk: number
}

type Properties = {
    w: number,
    h: number
}

function GerberFileEditor(props: Properties) {

    // dom related staff
    const canvasRef: MutableRefObject<HTMLCanvasElement | null> = useRef(null)
    const bgRef: MutableRefObject<HTMLDivElement | null> = useRef(null)
    const editableLayerRef: MutableRefObject<HTMLDivElement | null> = useRef(null)
    let ctxRef: MutableRefObject<CanvasRenderingContext2D | null> = useRef(null)

    let canvasRefEl: HTMLCanvasElement
    // let bgRefEl: HTMLDivElement
    let editableLayerRefEl: HTMLDivElement
    let ctxRefEl: CanvasRenderingContext2D

    // logic
    const cWidth = fileEditorStore.fieldSize.width
    const cHeight = fileEditorStore.fieldSize.height
    const selectedFileName = fileEditorStore.selectedFileName
    const fileChanged = initializedFileName !== selectedFileName

    // init
    let holesCommands = toJS(fileEditorStore.drillData)
        .map(dp => EditorCommandFactory.addHole(dp.point, dp.diameter / 10));
    // read zero from store only on file switch — otherwise SET_ZERO would re-render and was resetting zoom
    const initialZeroOffset = fileChanged
        ? {
            x: fileEditorStore.zeroOffsetX,
            y: fileEditorStore.zeroOffsetY
        }
        : (editorService.getZeroOffset() ?? {x: 0, y: 0})
    editorService.init(
        selectedFileName,
        holesCommands,
        initialZeroOffset
    )

    // reset view only when switching files (same as drill: in-file commands must not reset zoom)
    if (fileChanged) {
        initializedFileName = selectedFileName
        drawContext.zoom = drawDefaultContext.zoom
        drawContext.targetLeft = drawDefaultContext.targetLeft
        drawContext.targetTop = drawDefaultContext.targetTop
        resetBg()
    }

    useEffect(() => {
        //init canvas
        if (canvasRef.current != null) {
            canvasRefEl = canvasRef.current;
            const ctx = canvasRefEl.getContext("2d");
            if (ctx != null) {
                ctxRef.current = ctx
                ctxRefEl = ctxRef.current
            }
        } else {
            console.error("Error occurs when initializing canvas 2d")
        }

        // init background
        if (bgRef.current == null) {
            console.error("Error occurs when initializing bgRef")
        }

        // init background
        if (editableLayerRef.current == null) {
            console.error("Error occurs when initializing bgRef")
        } else {
            editableLayerRefEl = editableLayerRef.current
        }

        if (
            ctxRefEl != null &&
            bgRef.current != null &&
            editableLayerRefEl != null
        ) {
            window.addEventListener("keydown", handleKeyPress)
            document.body.style.overflow = "hidden";
            animate();
            return () => {
                window.removeEventListener("keydown", handleKeyPress)
                document.body.style.overflow = "scroll"
            };
        } else {
            console.error("Error occurs when initializing editor")
        }

    }, []);

    function resetBg() {
        if (bgRef.current != null) {
            bgRef.current.style.left = "0px"
            bgRef.current.style.top = "0px"
        }
    }

    function ctx() {
        return globalContext.editorContext()
    }

    function animate() {
        draw()
        requestAnimationFrame(animate)
    }

    // handle events
    function handleContextMenu(e: any) {
        console.log("handleContextMenu " + e)
        e.preventDefault()
    }

    function handleCanvasOnClick(e: any) {
        const stickedPoint = stickedPointer()
        const currentX = stickedPoint.x
        const currentY = cHeight - stickedPoint.y

        if (fileEditorStore.currentMode === EditorModeType.ROUTES || fileEditorStore.currentMode === EditorModeType.CUT) {
            if (drawContext.moveOriginX !== drawContext.currentX || drawContext.moveOriginY !== drawContext.currentY) {
                e.preventDefault()
                return
            }

            if (!drawContext.paintingMode) {
                editorService.startLine(
                    {
                        x: currentX,
                        y: currentY
                    },
                    ctx().toolDiameter,
                    fileEditorStore.currentMode
                )
                drawContext.paintingMode = true
            } else {
                editorService.continueLine(
                    {
                        x: currentX,
                        y: currentY
                    }
                )
            }
            e.preventDefault()
        }

        if (fileEditorStore.currentMode === EditorModeType.DRILL) {
            if (drawContext.moveOriginX !== drawContext.currentX || drawContext.moveOriginY !== drawContext.currentY) {
                e.preventDefault()
                return
            }
            if (drawContext.paintingMode) {
                editorService.finishLine()
                drawContext.paintingMode = false
            }
            editorService.drillHole(
                {
                    x: currentX,
                    y: currentY
                },
                ctx().toolDiameter
            )
            e.preventDefault()
        }

        if (fileEditorStore.currentMode === EditorModeType.G_CODE_ZERO) {
            if (drawContext.moveOriginX !== drawContext.currentX || drawContext.moveOriginY !== drawContext.currentY) {
                e.preventDefault()
                return
            }
            if (drawContext.paintingMode) {
                editorService.finishLine()
                drawContext.paintingMode = false
            }
            editorService.setZeroPoint({
                x: currentX,
                y: currentY
            })
            e.preventDefault()
        }
    }

    function handleKeyPress(e: any) {
        if (e.code === "Escape") {
            editorService.finishLine()
            drawContext.paintingMode = false
        }
        if (
            (e.code === "KeyZ" && e.metaKey && !e.shiftKey) ||
            (e.code === "KeyZ" && e.ctrlKey && !e.shiftKey)
        ) {
            editorService.cancel()
            drawContext.paintingMode = false
        }
        if (
            (e.code === "KeyZ" && e.metaKey && e.shiftKey) ||
            (e.code === "KeyZ" && e.ctrlKey && e.shiftKey)
        ) {
            editorService.unCancel()
            drawContext.paintingMode = false
        }
    }

    function handleCanvasOnMouseMove(e: any) {
        captureCoordinate(e)
        if (drawContext.moveMode) {
            const offsetX = (e.clientX - drawContext.moveOriginX) / drawContext.zoom
            const offsetY = (e.clientY - drawContext.moveOriginY) / drawContext.zoom
            drawContext.targetLeft = offsetX + drawContext.currentBgX
            drawContext.targetTop = offsetY + drawContext.currentBgY
        } else if (drawContext.paintingMode) {
            const stickedPoint = stickedPointer()
            drawContext.paintDataLastX = stickedPoint.x
            drawContext.paintDataLastY = cHeight - stickedPoint.y
        }
    }

    function handleCanvasOnWheel(e: any) {
        let tz = drawContext.zoom
        const eventDelta = -e.deltaY
        if (drawContext.zoom > minZoom && eventDelta < 0) {
            captureCurrentOffset(e)
            const delta = (eventDelta / zoomSpeed) * drawContext.zoom * zoomSpeedFactor
            tz = Math.max(drawContext.zoom + delta, minZoom)
        }
        if (drawContext.zoom < maxZoom && eventDelta > 0) {
            captureCurrentOffset(e)
            const delta = (eventDelta / zoomSpeed) * drawContext.zoom * zoomSpeedFactor
            tz = Math.min(drawContext.zoom + delta, maxZoom)
        }
        if (drawContext.zoom !== tz) {
            drawContext.zoom = tz
            const zoomDiff = (1 / drawContext.originZoom - 1 / drawContext.zoom)
            const deltaX = (drawContext.moveOriginX - pointerOffsetX) * zoomDiff
            const deltaY = (drawContext.moveOriginY - pointerOffsetY) * zoomDiff
            drawContext.targetLeft -= deltaX
            drawContext.targetTop -= deltaY
        }
    }

    function handleCanvasOnMouseDown(e: any) {
        captureCoordinate(e)
        captureCurrentOffset(e)
        drawContext.moveMode = true
    }

    function handleCanvasOnMouseUp(e: any) {
        captureCoordinate(e)
        drawContext.moveMode = false
    }

    function handleCanvasOnMouseOut(e: any) {
        drawContext.moveMode = false
    }

    function handleCanvasOnScroll(e: any) {
        console.log("handleCanvasOnScroll " + e)
        if (e.cancelable) e.preventDefault()
        e.preventDefault()
    }

    function handleResizeCap(e: any) {
        console.log("handleResizeCap " + e)
        if (e.cancelable) e.preventDefault()
    }

    function stickedPointer() {
        let gridConsts = calcGridConsts(true);
        const gk = gridConsts.gk
        const px = drawContext.pointerX + gridConsts.gk / 2
        const py = drawContext.pointerY + gridConsts.gk / 2

        const stickedPx = Math.round(px / gk) * gridConsts.gk + gridConsts.constAdjX
        const stickedPy = Math.round(py / gk) * gridConsts.gk + gridConsts.constAdjY

        let currentX = mapXFromSvgToContext(stickedPx)
        let currentY = mapYFromSvgToContext(stickedPy)
        return {
            x: currentX,
            y: currentY
        }
    }

    function captureCurrentOffset(e: any) {
        if (bgRef.current != null) {
            const currentLeft = bgRef.current.style.left
            const currentTop = bgRef.current.style.top
            drawContext.currentBgX = +currentLeft.substring(0, currentLeft.length - 2)
            drawContext.currentBgY = +currentTop.substring(0, currentTop.length - 2)

            drawContext.moveOriginX = e.clientX
            drawContext.moveOriginY = e.clientY
            drawContext.originZoom = drawContext.zoom
        }
        //
    }

    //
    function captureCoordinate(e: any) {
        const px = e.clientX - pointerOffsetX
        const py = e.clientY - pointerOffsetY
        drawContext.pointerX = px
        drawContext.pointerY = py
        drawContext.nonStickedPointerX = px
        drawContext.nonStickedPointerY = py

        drawContext.currentX = e.clientX
        drawContext.currentY = e.clientY
    }

    function calcGridConsts(calcOriginal = false) {
        const gridSize = ctx().gridSize
        let gk = gridSize * canvasToSvgKoeff * drawContext.zoom

        if (!calcOriginal) {
            while (gk < minGridSize) {
                gk = gk * 2
            }
        }

        const constAdjX = ((drawContext.targetLeft + ctx().zeroOffsetX) * drawContext.zoom) % gk
        const constAdjY = ((drawContext.targetTop + ctx().zeroOffsetY) * drawContext.zoom) % gk
        return {
            constAdjX: constAdjX,
            constAdjY: constAdjY,
            gk: gk
        }
    }

    // redraw canvas
    function draw() {
        ctxRefEl.clearRect(0, 0, canvasRefEl.width, canvasRefEl.height)
        ctxRefEl.lineWidth = 1
        ctxRefEl.globalAlpha = 1.0
        let gridConsts = calcGridConsts();
        drawPointer(ctxRefEl)
        drawGrid(canvasRefEl, ctxRefEl, gridConsts)
        drawZeroPoint(ctxRefEl)

        const paintData: PaintData = editorService.paintData()
        if (fileEditorStore.isActive) {
            drawLines(paintData.routes, ctxRefEl, EditorModeType.ROUTES)
            drawDrills(paintData.holes, ctxRefEl)
            drawLines(paintData.cuts, ctxRefEl, EditorModeType.CUT)
        }

        if (bgRef.current != null) {
            bgRef.current.style.left = drawContext.targetLeft + "px";
            bgRef.current.style.top = drawContext.targetTop + "px";
            (bgRef.current.style as any).zoom = drawContext.zoom;
        }
        if (editableLayerRef.current != null) {
            editableLayerRef.current.style.left = drawContext.targetLeft + "px";
            editableLayerRef.current.style.top = drawContext.targetTop + "px";
            (editableLayerRef.current.style as any).zoom = drawContext.zoom;
        }
    }

    function drawPointer(gctx: CanvasRenderingContext2D) {
        const stickedPoint = stickedPointer()
        let currentX
        let currentY
        currentX = mapXFromContextToSvg(stickedPoint.x)
        currentY = mapYFromContextToSvg(cHeight - stickedPoint.y)

        gctx.beginPath()
        gctx.strokeStyle = "#38c700"
        gctx.moveTo(currentX, currentY - pointerWidth * 2);
        gctx.lineTo(currentX, currentY + pointerWidth * 2);
        gctx.moveTo(currentX - pointerWidth * 2, currentY);
        gctx.lineTo(currentX + pointerWidth * 2, currentY);
        gctx.stroke()

        gctx.beginPath()
        gctx.strokeStyle = "#144400"
        gctx.rect(currentX - pointerWidth / 2, currentY - pointerWidth / 2, pointerWidth, pointerWidth)
        gctx.stroke()
    }

    function drawZeroPoint(gctx: CanvasRenderingContext2D) {
        if (!fileEditorStore.isActive) {
            return
        }
        const zeroX = mapXFromContextToSvg(ctx().zeroOffsetX)
        const zeroY = mapYFromContextToSvg(ctx().zeroOffsetY)
        const arm = pointerWidth * 2.5

        gctx.beginPath()
        gctx.strokeStyle = "#c70000"
        gctx.lineWidth = 2
        gctx.moveTo(zeroX - arm, zeroY)
        gctx.lineTo(zeroX + arm, zeroY)
        gctx.moveTo(zeroX, zeroY - arm)
        gctx.lineTo(zeroX, zeroY + arm)
        gctx.stroke()
        gctx.lineWidth = 1
    }

    function drawGrid(canvas: HTMLCanvasElement, gctx: CanvasRenderingContext2D, gridConsts: GridContext) {
        let countX = (canvas.width / gridConsts.gk)
        let countY = (canvas.height / gridConsts.gk)
        countX = countX - countX % 1 + 2
        countY = countY - countY % 1 + 2
        const pi2 = 2 * Math.PI
        gctx.strokeStyle = gridColor;
        for (let i = 0; i < countX * countY; i++) {
            const x = gridConsts.gk * (i % countX) + gridConsts.constAdjX
            const y = gridConsts.gk * ((i / countX) - (i / countX) % 1) + gridConsts.constAdjY

            gctx.beginPath();
            gctx.arc(x, y, 1, 0, pi2);
            gctx.stroke();
        }
    }

    function drawDrills(drillData: Array<DrillPoint>, gctx: CanvasRenderingContext2D) {
        gctx.globalAlpha = 0.8
        gctx.fillStyle = drillColor;
        drillData.forEach(dp => {
            gctx.beginPath();
            gctx.arc(
                mapXFromContextToSvg(dp.point.x),
                mapYFromContextToSvg(dp.point.y),
                dp.diameter * drawContext.zoom * 5,
                0,
                2 * Math.PI
            );
            gctx.fill();
        })
    }

    function drawLines(lines: Array<PolyLine>, gctx: CanvasRenderingContext2D, lineType: EditorModeType) {
        gctx.globalAlpha = 0.8
        if (lineType == EditorModeType.ROUTES) {
            gctx.strokeStyle = routesColor
        }
        if (lineType == EditorModeType.CUT) {
            gctx.strokeStyle = cutColor
        }
        gctx.lineCap = "round"
        gctx.lineJoin = "round"
        let mode = EditorModeType.ROUTES
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i]
            gctx.lineWidth = canvasToSvgKoeff * drawContext.zoom * line.width
            gctx.beginPath()
            gctx.moveTo(mapXFromContextToSvg(line.points[0].x), mapYFromContextToSvg(line.points[0].y))
            for (let j = 1; j < line.points.length; j++) {
                gctx.lineTo(mapXFromContextToSvg(line.points[j].x), mapYFromContextToSvg(line.points[j].y))
            }
            if (drawContext.paintingMode && i == (lines.length - 1) && lineType == fileEditorStore.currentMode) {
                gctx.lineTo(mapXFromContextToSvg(drawContext.paintDataLastX), mapYFromContextToSvg(drawContext.paintDataLastY))
            }
            gctx.stroke()
        }
    }

    function mapXFromContextToSvg(x: number): number {
        return drawContext.zoom * x + (drawContext.targetLeft * drawContext.zoom)
    }

    function mapYFromContextToSvg(y: number): number {
        return drawContext.zoom * y + (drawContext.targetTop * drawContext.zoom)
    }

    function mapXFromSvgToContext(x: number): number {
        return x / drawContext.zoom - drawContext.targetLeft
    }

    function mapYFromSvgToContext(y: number): number {
        return cHeight - (y / drawContext.zoom - drawContext.targetTop)
    }

    return (<>
        <EditorElement
            handleContextMenu={handleContextMenu}
            handleCanvasOnClick={handleCanvasOnClick}
            handleCanvasOnMouseMove={handleCanvasOnMouseMove}
            handleCanvasOnMouseOut={handleCanvasOnMouseOut}
            handleCanvasOnMouseDown={handleCanvasOnMouseDown}
            handleCanvasOnMouseUp={handleCanvasOnMouseUp}
            handleCanvasOnScroll={handleCanvasOnScroll}
            handleCanvasOnWheel={handleCanvasOnWheel}
            handleResizeCap={handleResizeCap}

            isActive={fileEditorStore.isActive}
            fileContent={fileEditorStore.fileContent}
            fieldSizeWidth={fileEditorStore.fieldSize.width}
            fieldSizeHeight={fileEditorStore.fieldSize.height}

            editableLayerRef={editableLayerRef}
            canvasRef={canvasRef}
            bgRef={bgRef}

            w={props.w}
            h={props.h}
        />
    </>)
}

export default observer(GerberFileEditor)
